import React, { useEffect, useState } from 'react';
import {
    Card, CardContent, Divider,
    Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow,
    TextField, Stack, CardHeader,
    Checkbox, Menu, MenuItem, Button,
    Dialog, DialogTitle, DialogContent, DialogActions, Typography, Grid, IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import moment from 'moment';

import { api } from "../../../services/Main/Api";
import { formatPhone } from "../../../helpers/formatters";
import PaginationPage from "../../../components/PaginationPage/PaginationPage";
import {getCondoId} from "../../../services/Main/AuthStorage";
import {useSnackbar} from "notistack";

const UserList = () => {
    const [search, setSearch] = useState('');
    const [users, setUsers] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [anchorEl, setAnchorEl] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);
    const [page, setPage] = useState(1);
    const [showSearch, setShowSearch] = useState(false);
    const [errors, setErrors] = useState({});
    const {enqueueSnackbar} = useSnackbar()

    const [newUser, setNewUser] = useState({
        first_name: '', last_name: '', email: '', phone_number: ''
    });

    const openMenu = Boolean(anchorEl);

    useEffect(() => {
        const filters = {
            search,
            page,
            page_size: 50,
            condo: getCondoId(),
        };

        api.get('user/', { params: filters })
            .then(response => {
                setUsers(response.data);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setSelectedIds([]);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [search, page]);

    const handleSelectAll = (event) => {
        const results = users?.results || [];
        setSelectedIds(event.target.checked ? results.map((u) => u.id) : []);
    };

    const handleSelectOne = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
        );
    };

    const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);
    const handleDeleteClick = () => { setConfirmOpen(true); handleMenuClose(); };
    const handleDeleteConfirmed = async () => {
        try {
            await Promise.all(
                selectedIds.map((id) => api.delete(`user/${id}/?condo=${getCondoId()}`))
            );

            const updated = users.results.filter((u) => !selectedIds.includes(u.id));
            setUsers((prev) => ({ ...prev, results: updated }));
            enqueueSnackbar("Usuário foi deletado com sucesso!", {variant: 'success'});
        } catch (error) {
            console.error('Erro ao deletar usuários:', error);
            enqueueSnackbar(`Erro ao deletar usuários:${error}`, {variant: 'error'});
            // Aqui você pode adicionar um snackbar ou alerta visual se quiser
        } finally {
            setSelectedIds([]);
            setConfirmOpen(false);
        }
    };


    const handleRowClick = (usuario) => {
        setNewUser(usuario);
        setEditId(usuario.id);
        setEditMode(true);
        setDialogOpen(true);
    };

    const handleDialogChange = (e) => {
        const { name, value } = e.target;
        setNewUser((prev) => ({ ...prev, [name]: value }));
    };

    const validateFields = () => {
        const required = ['first_name', 'last_name', 'email'];
        const newErrors = {};
        required.forEach((field) => {
            if (!newUser[field]?.trim()) newErrors[field] = 'Campo obrigatório';
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSaveUser = () => {
        if (!validateFields()) return;

        const payload = { ...newUser };

        const clearForm = () => {
            setNewUser({ first_name: '', last_name: '', email: '', phone_number: '' });
            setErrors({});
        };

        if (editMode) {
            api.put(`user/${editId}/?condo=${getCondoId()}`, payload)
                .then(response => {
                    const updatedResults = users.results.map(u => u.id === editId ? response.data : u);
                    setUsers({ ...users, results: updatedResults });
                    setDialogOpen(false);
                    clearForm();
                    setEditMode(false);
                    enqueueSnackbar("Usuário foi atualizado com sucesso!", {variant: 'success'});
                });
        } else {
            api.post(`user/?condo=${getCondoId()}`, payload)
                .then((response) => {
                    setUsers((prev) => ({
                        ...prev,
                        results: [...(prev.results || []), response.data]
                    }));
                    setDialogOpen(false);
                    clearForm();
                    enqueueSnackbar("Usuário foi criado com sucesso!", {variant: 'success'});
                });
        }
    };

    const isSelected = (id) => selectedIds.includes(id);
    const allSelected = users.results && users.results.length > 0 && selectedIds.length === users.results.length;

    return (
        <>
            <Card sx={{ margin: 2 }}>
                <CardHeader title={`Usuários (${users?.count || 0})`} />
                <Divider sx={{ mb: 2 }} />

                <Stack direction="row" sx={{ px: 2 }} spacing={2} alignItems="center">
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setDialogOpen(true);
                            setEditMode(false);
                            setNewUser({ first_name: '', last_name: '', email: '', phone_number: '' });
                        }}
                    >
                        Criar Usuário
                    </Button>

                    {showSearch ? (
                        <TextField
                            size="small"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Pesquisar..."
                            autoFocus
                            onBlur={() => {
                                if (!search.trim()) setShowSearch(false);
                            }}
                            InputProps={{
                                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} />,
                            }}
                        />
                    ) : (
                        <IconButton onClick={() => setShowSearch(true)}>
                            <SearchIcon />
                        </IconButton>
                    )}

                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<KeyboardArrowDownIcon />}
                        onClick={handleMenuClick}
                        disabled={selectedIds.length === 0}
                    >
                        Ações {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
                    </Button>
                    <Menu anchorEl={anchorEl} open={openMenu} onClose={handleMenuClose}>
                        <MenuItem onClick={handleDeleteClick}>
                            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                            Excluir selecionados
                        </MenuItem>
                    </Menu>
                </Stack>

                <CardContent>
                    {loading ? (
                        <Typography variant="body2">Carregando usuários...</Typography>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox checked={allSelected} onChange={handleSelectAll} />
                                        </TableCell>
                                        <TableCell><strong>Nome</strong></TableCell>
                                        <TableCell><strong>Email</strong></TableCell>
                                        <TableCell><strong>Telefone</strong></TableCell>
                                        <TableCell><strong>Criado em</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {users?.results?.map((usuario) => (
                                        <TableRow key={usuario.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleRowClick(usuario)}>
                                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                                <Checkbox checked={isSelected(usuario.id)} onChange={() => handleSelectOne(usuario.id)} />
                                            </TableCell>
                                            <TableCell>{usuario.first_name} {usuario.last_name}</TableCell>
                                            <TableCell>{usuario.email}</TableCell>
                                            <TableCell>{formatPhone(usuario.phone_number)}</TableCell>
                                            <TableCell>{moment(usuario.created_at).format("DD/MM/YYYY [às] HH:mm")}</TableCell>
                                        </TableRow>
                                    ))}
                                    {users.results?.length === 0 && (
                                        <TableRow><TableCell colSpan={5} align="center">Nenhum usuário encontrado.</TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            <PaginationPage data={{ ...users, page }} onChange={setPage} />

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <Typography>Tem certeza que deseja deletar {selectedIds.length} {selectedIds.length === 1 ? 'item' : 'itens'}?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} size="small">Cancelar</Button>
                    <Button onClick={handleDeleteConfirmed} color="error" variant="contained" size="small">Deletar</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editMode ? 'Editar Usuário' : 'Novo Usuário'}</DialogTitle>
                <Divider />
                <DialogContent>
                    <Grid container spacing={2} mt={1}>
                        {['first_name', 'last_name', 'email', 'phone_number'].map((field) => (
                            <Grid item xs={12} sm={6} key={field}>
                                <TextField
                                    label={{
                                        first_name: 'Nome',
                                        last_name: 'Sobrenome',
                                        email: 'Email',
                                        phone_number: 'Telefone'
                                    }[field]}
                                    name={field}
                                    value={newUser[field] || ''}
                                    onChange={handleDialogChange}
                                    fullWidth
                                    size="small"
                                    error={!!errors[field]}
                                    helperText={errors[field]}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </DialogContent>
                <Divider />
                <DialogActions sx={{ py: 3, display: 'flex', justifyContent: 'space-between' }}>
                    <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button onClick={handleSaveUser} variant="contained">Salvar</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UserList;
